import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { api, type ReportScope } from "../../../lib/api";
import { queryKeys } from "../../../lib/queryKeys";
import { DASHBOARD_STALE_MS } from "../../../lib/dashboard-refresh";
import { formatMinutes } from "../RangeFilter";
import { cn } from "../../ui/cn";

/**
 * Overdue Lane tasks, as an alert rather than a report.
 *
 * The only card on the dashboard that is not a `ReportCard`, and deliberately.
 * Everything else here answers "how are we doing"; this one says "these eleven
 * things are late", and wrapping it in a date filter and a chart switcher would
 * dress an alert up as another chart to skim past. There is nothing to
 * visualise and no period to choose — a task is overdue now or it is not.
 *
 * It also renders nothing at all when nothing is late. A permanent banner
 * showing zero is a banner people stop seeing, and then it is still invisible
 * on the day it says eleven.
 *
 * Board, then the people on it, then their tasks. A board with thirty late
 * tasks used to open into thirty lines with a name repeated down the
 * right-hand edge, which answers "what is late" but not the question a manager
 * is actually asking, which is *whose*. Grouping by person turns the same rows
 * into "Rachel has nine" -- and one more click still gets the nine.
 *
 * **Unless the dashboard is already filtered to one person**, in which case the
 * middle level is that person's name, repeated, with every task underneath it.
 * It is a click that can only ever have one answer. Looking at Rachel, the
 * useful shape is the one the board level already gives -- "Courier Issues, 9"
 * -- opening straight onto her nine tasks. So the person level is dropped
 * entirely and the card is two levels deep instead of three.
 *
 * The grouping is chosen from the scope rather than from the data: a person
 * whose overdue tasks all happen to sit on one board is not the same situation
 * as a dashboard filtered to them, and collapsing the level because a count
 * came back as one would make the card change shape as work moved around.
 */
/** What an unassigned task is filed under. */
const UNASSIGNED = "Nobody assigned";

interface OverdueTask {
  id: string;
  title: string;
  url: string;
  dueAt: string;
  /** Minutes, not days -- see the note on the lateness column below. */
  minutesLate: number;
  assigneeNames: string[];
}

/**
 * Tasks by the person holding them, worst pile first.
 *
 * A task with two assignees counts for both. That double-counts against the
 * board total and is still right: the question this answers is "how much is
 * late on my plate", and being one of two people responsible does not make it
 * half a task.
 */
function byPerson(tasks: readonly OverdueTask[]): [string, OverdueTask[]][] {
  const people = new Map<string, OverdueTask[]>();

  for (const task of tasks) {
    const names =
      task.assigneeNames.length > 0 ? task.assigneeNames : [UNASSIGNED];
    for (const name of names) {
      const bucket = people.get(name) ?? [];
      bucket.push(task);
      people.set(name, bucket);
    }
  }

  return [...people.entries()].sort(
    (a, b) =>
      /* Unassigned last: it is a different problem from somebody being behind,
         and it is already coloured to stand out. */
      Number(a[0] === UNASSIGNED) - Number(b[0] === UNASSIGNED) ||
      b[1].length - a[1].length ||
      a[0].localeCompare(b[0]),
  );
}

/**
 * The individual tasks, which is where every path through this card ends.
 *
 * Extracted so the two shapes -- board/person/task and board/task -- render the
 * leaf identically. Inlining it in both branches is how the filtered view ends
 * up quietly missing the lateness column six months from now.
 */
function TaskList({ tasks }: { tasks: readonly OverdueTask[] }) {
  return (
    <ul className="pb-1 pl-5">
      {tasks.map((task) => (
        <li key={task.id} className="flex items-baseline gap-2 py-0.5">
          {/* Straight into Lane. The task is the work; Pulse only knows
              that it is late. */}
          <a
            href={task.url}
            target="_blank"
            rel="noreferrer"
            className="min-w-0 flex-1 truncate text-xs text-brand-600 underline decoration-brand-300 underline-offset-2 hover:text-brand-700"
          >
            {task.title}
            <span aria-hidden className="ml-1 text-[10px] no-underline">
              ↗
            </span>
          </a>
          {/*
            How late, to the hour, in the format every other card on this
            dashboard uses -- "40m", "3h 20m", "2d 5h".

            It used to be whole days, with anything under one printed as
            "today", and that was wrong rather than merely coarse. A due date
            carries a real time: the task dialog sets 17:00 on the day
            somebody picked, and the CRM's shortcuts set the hour they were
            pressed. So a task due at five yesterday said "today" all the next
            morning -- the one word that reads as "not late yet" -- in a banner
            whose whole job is to say what is late.
          */}
          <span
            className="shrink-0 text-[11px] tabular-nums text-muted"
            title={`Due ${new Date(task.dueAt).toLocaleString()}`}
          >
            {formatMinutes(task.minutesLate)} overdue
          </span>
        </li>
      ))}
    </ul>
  );
}

export function OverdueTasks({ scope }: { scope: ReportScope }) {
  const [openBoard, setOpenBoard] = useState<string | null>(null);
  /* Keyed by board and person together, so opening somebody on one board does
     not silently collapse a same-named person on another. */
  const [openPerson, setOpenPerson] = useState<string | null>(null);

  /*
   * Whether the person level earns its place. See the note at the top: this
   * asks the scope, not the data, so the card does not change shape as tasks
   * move between people.
   */
  const groupByPerson = !scope.userId;

  const overdue = useQuery({
    queryKey: queryKeys.dashboardReport("lane-overdue", null, scope),
    queryFn: () => api.overdueTasks(scope),
    /*
     * A minute. This reads a dozen boards out of another product on every call,
     * so it is the most expensive thing on the page -- and a task that went
     * overdue thirty seconds ago is not more actionable than one that went
     * overdue a minute ago.
     */
    staleTime: DASHBOARD_STALE_MS,
  });

  const data = overdue.data;

  /* Nothing while loading, nothing when Lane is off, nothing when clear. */
  if (!data?.enabled || data.total === 0) return null;

  return (
    <section className="rounded-lg border border-sla-warning/50 bg-sla-warning/10">
      <header className="flex flex-wrap items-center gap-2 px-3 py-2">
        <span className="text-sm font-medium text-content">
          {data.total} overdue {data.total === 1 ? "task" : "tasks"} in Lane
        </span>
        <span className="text-[11px] text-muted">
          {scope.userId ? "assigned to this person" : "across the boards"}
        </span>

        {/* Said rather than swallowed: a short count that looks complete is
            worse than one that admits what it could not read. */}
        {data.unreadable > 0 ? (
          <span className="text-[11px] text-faint">
            {data.unreadable} board{data.unreadable === 1 ? "" : "s"} could not
            be read
          </span>
        ) : null}
      </header>

      <ul className="border-t border-sla-warning/30">
        {data.groups.map((group) => {
          const expanded = openBoard === group.board;
          const people = groupByPerson ? byPerson(group.tasks) : [];

          return (
            <li
              key={group.board}
              className="border-b border-sla-warning/20 last:border-0"
            >
              <button
                type="button"
                onClick={() => setOpenBoard(expanded ? null : group.board)}
                aria-expanded={expanded}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-sla-warning/10"
              >
                <span
                  aria-hidden
                  className={cn(
                    "text-xs text-faint transition-transform",
                    expanded && "rotate-90",
                  )}
                >
                  ▶
                </span>
                <span className="min-w-0 flex-1 truncate text-xs text-content">
                  {group.board}
                </span>
                <span className="shrink-0 text-xs tabular-nums text-muted">
                  {group.tasks.length}
                </span>
              </button>

              {/* Filtered to one person: straight to their tasks. The level
                  that would sit here is their name, once, with all of these
                  underneath it. */}
              {expanded && !groupByPerson ? (
                <div className="pb-1.5 pl-8 pr-3">
                  <TaskList tasks={group.tasks} />
                </div>
              ) : null}

              {expanded && groupByPerson ? (
                <ul className="pb-1.5 pl-8 pr-3">
                  {people.map(([person, tasks]) => {
                    const key = `${group.board}::${person}`;
                    const showing = openPerson === key;

                    return (
                      <li key={key}>
                        <button
                          type="button"
                          onClick={() => setOpenPerson(showing ? null : key)}
                          aria-expanded={showing}
                          className="flex w-full items-center gap-2 py-0.5 text-left hover:bg-sla-warning/10"
                        >
                          <span
                            aria-hidden
                            className={cn(
                              "text-[10px] text-faint transition-transform",
                              showing && "rotate-90",
                            )}
                          >
                            ▶
                          </span>
                          <span
                            className={cn(
                              "min-w-0 flex-1 truncate text-xs",
                              person === UNASSIGNED
                                ? "text-sla-breached"
                                : "text-content",
                            )}
                          >
                            {person}
                          </span>
                          <span className="shrink-0 text-xs tabular-nums text-muted">
                            {tasks.length}
                          </span>
                        </button>

                        {showing ? <TaskList tasks={tasks} /> : null}
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
